import { useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useNavHeight } from '../hooks/useNavHeight'
import { Compass, Search } from 'lucide-react'

// ── GuestNav ──────────────────────────────────────────────────────────────────
// Logged-out visitors can already reach every browse page (show detail, Discover,
// Search all render without an account) — but they had NO way to move between them:
// each page carried its own bare top bar, so a guest who landed somewhere was stuck.
// This is the persistent bottom nav for guests: Discover + Search to wander freely,
// and an always-present "Sign up free" so the conversion CTA follows them around.
// Rendered only when there's no user and not on the landing/login pages (App.jsx).
// (Jason 2026-07-23 — "browse without an account", parity with the field.)
export function GuestNav() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // The guest nav is a DIFFERENT height from the signed-in one, and since 15.793 a
  // signed-out visitor reaches real surfaces (an Aftershow room), so it has to publish
  // --nav-h too or those pages reserve the wrong amount of room.
  const navRef = useRef(null)
  useNavHeight(navRef)

  const tab = (to, Icon, label) => {
    const active = pathname.startsWith(to)
    return (
      <button onClick={() => navigate(to)} style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
        background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)',
        color: active ? 'var(--amber)' : 'var(--text-dim)', padding: '5px 14px', flexShrink: 0,
      }}>
        <Icon size={20} strokeWidth={active ? 2.5 : 2} />
        <span style={{ fontSize: 10.5, fontWeight: active ? 700 : 500 }}>{label}</span>
      </button>
    )
  }

  return (
    <nav ref={navRef} style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 997,
      background: 'var(--nav-bg)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
      borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 14px calc(8px + env(safe-area-inset-bottom))', fontFamily: 'var(--font)',
    }}>
      {tab('/discover', Compass, 'Discover')}
      {tab('/search', Search, 'Search')}
      {/* Ever-present conversion CTA, goes to the no-account /start path. */}
      <button onClick={() => navigate('/start')} className="ui-primary" style={{
        flex: 1, marginLeft: 4, background: 'var(--grad-amber)', border: 'none',
        borderRadius: 'var(--radius-sm)', color: 'var(--amber-ink)', fontWeight: 800, fontSize: 13.5,
        padding: '11px 0', cursor: 'pointer', fontFamily: 'var(--font)',
      }}>Track your shows</button>
    </nav>
  )
}
